import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { format } from "date-fns";
import { Hospital, Pencil } from "lucide-react";
import { AdmittingPrivilegesForm } from "./AdmittingPrivilegesForm";

type AdmittingPrivilege = {
  index: number;
  value: unknown;
};
type AdmittingPrivilegeValue = {
  facility_name?: string;
  city?: string;
  state?: string;
  start_date?: string | Date;
  end_date?: string | Date | null;
};

export function AdmittingPrivilegesCard({
  editable,
  isEditing,
  onEditChange,
  admittingPrivileges,
  userId,
}: {
  editable: boolean;
  isEditing: boolean;
  onEditChange: (editing: boolean) => void;
  admittingPrivileges: AdmittingPrivilege[];
  userId: string;
}) {
  return (
    <Card
      className={cn("mt-4", {
        "shadow-none border-none": isEditing,
      })}
    >
      <CardHeader className="pb-1">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold">Admitting Privileges</h3>
          {isEditing || !editable ? null : (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onEditChange(true)}
            >
              <Pencil className="h-4 w-4" />
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {isEditing ? (
          <AdmittingPrivilegesForm
            admittingPrivileges={admittingPrivileges}
            userId={userId}
            onSave={() => onEditChange(false)}
          />
        ) : (
          <>
            {admittingPrivileges.map((privilege, index) => {
              const value = privilege.value as AdmittingPrivilegeValue;
              return (
                <div key={index} className="space-y-2">
                  <div className="flex items-center gap-3">
                    <Hospital className="h-5 w-5 text-gray-500" />
                    <div>
                      <div className="font-semibold">{value.facility_name}</div>
                      <div className="text-sm text-gray-500">
                        {value.city}, {value.state}
                      </div>
                      {value.start_date && (
                        <div className="text-sm text-gray-500">
                          {format(new Date(value.start_date), "MMM yyyy")} -{" "}
                          {value.end_date
                            ? format(new Date(value.end_date), "MMM yyyy")
                            : "Present"}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
            {admittingPrivileges.length === 0 && (
              <div className="text-center text-gray-500">
                No admitting privileges added yet.
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
